/**
 * CR-6205: the Files control on each line of the RFQ Create Wizard.
 *
 * Files picked here stay in wizard state and are uploaded onto the line right
 * after it is created (see rfqWizardSubmit). A File can't go in the saved
 * draft, so a resumed draft lists each file that was on the line in amber
 * until it is picked again or removed — the same rule as the NDA file, and
 * the wizard won't leave Line Items while any are listed.
 */
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { AlertTriangle, Paperclip, Trash2, X } from 'lucide-react';
import FileDropZone from '../common/FileDropZone';
import { MAX_ATTACHMENT_BYTES, formatBytes } from './rfqDocumentFiles';

interface LineFilesPickerProps {
  /** The line's position, for labels ("line 2"). */
  lineNumber: number;
  /** Files picked for the line so far. */
  files: File[];
  /** Files that were on the line when its draft was saved, still to pick again. */
  repickNames: string[];
  onChange: (files: File[], repickNames: string[]) => void;
}

export default function LineFilesPicker({ lineNumber, files, repickNames, onChange }: LineFilesPickerProps) {
  // Picking a file with a name already on the line replaces it, and picking
  // a file the saved draft listed clears that reminder.
  const add = (picked: File[]) => {
    const pickedNames = new Set(picked.map((file) => file.name));
    onChange(
      [...files.filter((file) => !pickedNames.has(file.name)), ...picked],
      repickNames.filter((name) => !pickedNames.has(name)),
    );
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">Files</Label>
      <FileDropZone
        size="compact"
        multiple
        maxSize={MAX_ATTACHMENT_BYTES}
        hint="Drawings or specifications for this line. Suppliers can download them. Up to 10 MB each."
        onFiles={add}
        inputTestId={`line-${lineNumber}-file-input`}
        aria-label={`Attach files to line ${lineNumber}`}
      />
      {repickNames.map((name) => (
        <div
          key={`repick-${name}`}
          className="flex items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm"
        >
          <span className="flex min-w-0 items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
            <span className="min-w-0">
              <span className="font-medium break-all">{name}</span>
              {' '}wasn&apos;t kept in the saved draft. Pick it again, or remove it.
            </span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove ${name} from line ${lineNumber}`}
            onClick={() => onChange(files, repickNames.filter((n) => n !== name))}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {files.map((file) => (
        <div key={file.name} className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
          <span className="flex min-w-0 items-center gap-2">
            <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate" title={file.name}>{file.name}</span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatBytes(file.size)}</span>
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Remove ${file.name} from line ${lineNumber}`}
            onClick={() => onChange(files.filter((f) => f !== file), repickNames)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
    </div>
  );
}
